/**
 * CCS Booking Theme — Minimal UI Kit + CCS Brand Palette
 *
 * Creates a Minimal-based theme for the booking wizard. Accepts an optional
 * `primaryColor` for per-pharmacy runtime branding (passed via ?primaryColor=
 * URL param). Falls back to katanaPurple when no override is provided.
 *
 * All other Minimal tokens (typography, shadows, border radius, component
 * overrides) are inherited unchanged from createMinimalTheme.
 */

import { createTheme } from "@mui/material/styles";
import type { Theme, Shadows } from "@mui/material/styles";

const FONT_PRIMARY = "'Public Sans', sans-serif";
const KATANA_PURPLE = "#7C3AED";
const KATANA_TEAL = "#4ECDC4";
const GREY_500 = "#919EAB";

function rem(px: number): string {
  return `${px / 16}rem`;
}

function shadow(opacity: number): string {
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, "0");
  return `0 0 2px 0 ${GREY_500}${alpha}, 0 12px 24px -4px ${GREY_500}${alpha}`;
}

const MINIMAL_SHADOWS: Shadows = [
  "none",
  `0 1px 2px 0 ${GREY_500}40`,
  shadow(0.14),
  shadow(0.16),
  shadow(0.18),
  shadow(0.20),
  shadow(0.22),
  shadow(0.24),
  shadow(0.26),
  shadow(0.28),
  shadow(0.30),
  shadow(0.32),
  shadow(0.34),
  shadow(0.36),
  shadow(0.38),
  shadow(0.40),
  shadow(0.42),
  shadow(0.44),
  shadow(0.46),
  shadow(0.48),
  shadow(0.50),
  shadow(0.52),
  shadow(0.54),
  shadow(0.56),
  shadow(0.58),
];

/**
 * Create the CCS booking theme.
 *
 * @param primaryColor  Optional per-pharmacy primary colour override (hex string).
 *                      Must start with "#". Defaults to CCS katanaPurple.
 */
export function createBookingTheme(primaryColor: string = KATANA_PURPLE): Theme {
  const base = createTheme({
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: FONT_PRIMARY,
      fontWeightRegular: 400,
      fontWeightMedium: 500,

      fontWeightBold: 700,
      h1: { fontWeight: 800, fontSize: rem(80) },
      h2: { fontWeight: 800, fontSize: rem(64) },
      h3: { fontWeight: 700, fontSize: rem(48) },
      h4: { fontWeight: 700, fontSize: rem(32) },
      h5: { fontWeight: 700, fontSize: rem(24), lineHeight: 36 / 24 },
      h6: { fontWeight: 700, fontSize: rem(18), lineHeight: 28 / 18 },
      subtitle1: { fontWeight: 600, fontSize: rem(16) },
      subtitle2: { fontWeight: 600, fontSize: rem(14) },
      body1: { fontWeight: 400, fontSize: rem(16) },
      body2: { fontWeight: 400, fontSize: rem(14) },
      caption: { fontWeight: 400, fontSize: rem(12) },
      overline: {
        fontWeight: 700,
        fontSize: rem(12),
        letterSpacing: "1.1px",
        textTransform: "uppercase",
      },
      button: { fontWeight: 700, textTransform: "unset" },
    },
    shadows: MINIMAL_SHADOWS,
    palette: {
      primary: {
        main: primaryColor,
        contrastText: "#ffffff",
      },
      secondary: {
        main: KATANA_TEAL,
        contrastText: "#ffffff",
      },
      background: {
        default: "#F9FAFB",
        paper: "#FFFFFF",
      },
      grey: {
        100: "#F9FAFB",
        200: "#F4F6F8",
        300: "#DFE3E8",
        400: "#C4CDD5",
        500: "#919EAB",
        600: "#637381",
        700: "#454F5B",
        800: "#212B36",
        900: "#161C24",
      },
    },
  });

  return createTheme(base, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*, *::before, *::after": { boxSizing: "border-box" },
          html: { WebkitFontSmoothing: "antialiased" },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 700 },
          sizeLarge: { minHeight: 48, padding: "8px 22px" },
          sizeMedium: { padding: "6px 16px" },
          sizeSmall: { padding: "4px 10px" },
          outlined: { borderColor: base.palette.divider },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: "none" },
          outlined: { borderColor: base.palette.divider, borderRadius: 12 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 16, boxShadow: base.shadows[2] },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8 },
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
    },
  });
}
